import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  MemberEvents,
  WorkspaceEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";
import { runtimeConfig } from "@src/runtime-config";
import { QRCodeDialogLocation } from "@utils/track-event/event-types";

/**
 * The different types of impersonation targets
 * "workspaceViewer" - log in as workspace viewer
 * "user" - log in as a specific user
 */
export type ImpersonationTarget = "workspaceViewer" | "user";

/**
 * The different modes to launch impersonation
 * "currentTab" - open the impersonation link in the current tab
 * "copyLink" - copy the impersonation link to clipboard
 */
export type ImpersonationLaunchMode = "currentTab" | "copyLink";

export const impersonationLaunchModeDescription: Record<
  ImpersonationLaunchMode,
  string
> = {
  currentTab: "Open in current tab",
  copyLink: "View login link",
};

/** Hook to track impersonation events */
export function useImpersonationTrackEvent(type: ImpersonationTarget): {
  trackImpersonation: (loginMode: ImpersonationLaunchMode) => Promise<void>;
} {
  const { trackAsyncEvent } = useTrackEvent();

  const trackImpersonation = useCallback(
    async (loginMode: ImpersonationLaunchMode) => {
      let name: WorkspaceEvents | MemberEvents;
      switch (type) {
        case "workspaceViewer":
          name = WorkspaceEvents.loginAsWorkspaceViewer;
          break;
        case "user":
          name = MemberEvents.loginAsMember;
          break;

        default: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const _exhaustiveCheck: never = type;
          throw new Error(`Unsupported login as type: ${_exhaustiveCheck}`);
        }
      }

      // Needed to wait for the event to be tracked before navigating away while opening in current tab
      await trackAsyncEvent({
        name,
        props: {
          method: impersonationLaunchModeDescription[loginMode],
        },
      });
    },
    [trackAsyncEvent, type]
  );

  return {
    trackImpersonation,
  };
}

/** Generate human readable from impersonation target */
export function formatImpersonationTarget(
  type: ImpersonationTarget
): QRCodeDialogLocation {
  switch (type) {
    case "user":
      return "login as member";
    case "workspaceViewer":
      return "login as workspace viewer";
    default: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _exhaustiveCheck: never = type;
      throw new Error(`Unsupported login as type: ${_exhaustiveCheck}`);
    }
  }
}

/** Get impersonation access link for the given target based on the type */
export async function getImpersonationTargetLabel(
  type: ImpersonationTarget,
  subjectId: string,
  redirectUrl?: string
): Promise<string> {
  switch (type) {
    case "workspaceViewer": {
      const { accessLink } =
        await faroCoreApiClient.admin.getWorkspaceViewerAccessLink({
          companyId: subjectId,
        });

      return accessLink;
    }

    case "user": {
      const userLogInAccessLink =
        await faroCoreApiClient.admin.getUserLogInAccessLink({
          memberId: subjectId,
        });

      const url = new URL(userLogInAccessLink, runtimeConfig.urls.apiUrl);

      url.searchParams.set(
        "redirect",
        redirectUrl ?? runtimeConfig.urls.dashboardUrl
      );

      return url.toString();
    }

    default: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _exhaustiveCheck: never = type;
      throw new Error(`Unsupported login as type: ${_exhaustiveCheck}`);
    }
  }
}
